// src/hooks/useExpenses.js
import { useCallback, useMemo, useState } from "react";

import {
  getExpensesApi,
  createExpenseApi,
  updateExpenseApi,
  deleteExpenseApi,
  getExpenseCategoriesApi,
} from "../api/expenses";
import { useAuth } from "./useAuth";

/**
 * Hook dedicado a Gastos (Expenses)
 * - Paginación server-side (DRF): { count, results }
 * - Filtros por querystring (params)
 */
export function useExpenses() {
  // =========================================================
  // 🔴 AJUSTA SOLO ESTA LÍNEA a tu useAuth real (sin cambiarlo)
  // =========================================================
  const { auth } = useAuth(); // <-- si tu hook entrega otro nombre, reemplázalo aquí
  const token = auth?.token;
  // =========================================================

  const [expenses, setExpenses] = useState([]);
  const [expensesCount, setExpensesCount] = useState(0);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [errorExpenses, setErrorExpenses] = useState(null);

  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loadingExpenseCategories, setLoadingExpenseCategories] = useState(false);
  const [errorExpenseCategories, setErrorExpenseCategories] = useState(null);

  const isReady = useMemo(() => Boolean(token), [token]);

  const getExpenses = useCallback(
    async (params = {}) => {
      if (!token) return;

      try {
        setLoadingExpenses(true);
        setErrorExpenses(null);

        const data = await getExpensesApi(token, params);

        // DRF paginated response
        setExpenses(Array.isArray(data?.results) ? data.results : []);
        setExpensesCount(typeof data?.count === "number" ? data.count : 0);

        return data;
      } catch (err) {
        console.error("Error fetching expenses:", err);
        setErrorExpenses(err);
        throw err;
      } finally {
        setLoadingExpenses(false);
      }
    },
    [token]
  );

  const createExpense = useCallback(
    async (payload) => {
      if (!token) return;
      return await createExpenseApi(payload, token);
    },
    [token]
  );

  const updateExpense = useCallback(
    async (id, payload) => {
      if (!token) return;
      return await updateExpenseApi(id, payload, token);
    },
    [token]
  );

  const removeExpense = useCallback(
    async (id) => {
      if (!token) return;
      return await deleteExpenseApi(id, token);
    },
    [token]
  );

  const getExpenseCategories = useCallback(
    async (params = {}) => {
      if (!token) return;

      try {
        setLoadingExpenseCategories(true);
        setErrorExpenseCategories(null);

        const data = await getExpenseCategoriesApi(token, params);

        // podría venir paginado o como array según tu backend:
        const categories = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data)
          ? data
          : [];

        setExpenseCategories(categories);
        return data;
      } catch (err) {
        console.error("Error fetching expense categories:", err);
        setErrorExpenseCategories(err);
        throw err;
      } finally {
        setLoadingExpenseCategories(false);
      }
    },
    [token]
  );

  return {
    // auth readiness
    isReady,

    // expenses list
    expenses,
    expensesCount,
    loadingExpenses,
    errorExpenses,
    getExpenses,

    // expenses CRUD
    createExpense,
    updateExpense,
    removeExpense,

    // categories (para filtros)
    expenseCategories,
    loadingExpenseCategories,
    errorExpenseCategories,
    getExpenseCategories,
  };
}
